import React, { useMemo, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import { Button, Select } from "../../../shared/ui/composites";
import { prettyJs } from "../utils/prettyJs";
import { CODE_SNIPPETS } from "../utils/taskScripts";

function previewLines(code, count = 4) {
  const lines = String(code || "")
    .split("\n")
    .filter((line, i, arr) => line.trim() || (i > 0 && i < arr.length - 1));
  const shown = lines.slice(0, count).join("\n");
  return lines.length > count ? `${shown}\n…` : shown || "// خالی";
}

export default function JobCodeEditor({
  id,
  value,
  onChange,
  disabled = false,
  error = false,
  title = "کد",
  collapsed = true,
  defaultOpen = false,
}) {
  const textareaRef = useRef(null);
  const [open, setOpen] = useState(!collapsed || defaultOpen);
  const code = value ?? "";
  const lineCount = useMemo(
    () => Math.max(String(code).split("\n").length, 8),
    [code],
  );

  const insertSnippet = (snippet) => {
    if (!snippet || disabled) return;
    const el = textareaRef.current;
    const start = el?.selectionStart ?? String(code).length;
    const end = el?.selectionEnd ?? start;
    const next = `${String(code).slice(0, start)}${snippet}\n${String(code).slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + snippet.length + 1;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Tab" || disabled) return;
    e.preventDefault();
    const el = e.target;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = `${code.slice(0, start)}  ${code.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + 2;
    });
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-right"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-xs text-slate-500">
            {open ? "▾" : "▸"}
          </span>
          {title}
        </span>
        <span className="text-[11px] text-slate-400">
          {open ? "بستن" : "باز کردن برای ویرایش"}
        </span>
      </button>
      {!open && (
        <pre
          dir="ltr"
          className="mb-0 max-h-24 overflow-hidden border-t border-slate-100 bg-slate-950 px-3 py-2 text-left font-mono text-[11px] leading-5 text-emerald-200/80"
        >
          {previewLines(code)}
        </pre>
      )}
      {open && (
        <div className="space-y-2 border-t border-slate-100 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => onChange(prettyJs(code))}
            >
              زیباسازی کد
            </Button>
            <Select
              variant="filter"
              className="min-w-[12rem]"
              disabled={disabled}
              value=""
              onChange={(e) => {
                const snippet = CODE_SNIPPETS.find((s) => s.id === e.target.value);
                if (snippet) insertSnippet(snippet.code);
                e.target.value = "";
              }}
            >
              <option value="">درج کتابخانه…</option>
              {CODE_SNIPPETS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
          <div
            dir="ltr"
            className={cn(
              "flex overflow-hidden rounded-lg border bg-slate-950 text-slate-100",
              error ? "border-red-400" : "border-slate-700",
            )}
          >
            <pre
              aria-hidden="true"
              className="select-none border-r border-slate-800 bg-slate-900 px-2 py-3 text-right font-mono text-[11px] leading-5 text-slate-500"
            >
              {Array.from({ length: lineCount }, (_, i) => i + 1).join("\n")}
            </pre>
            <textarea
              id={id}
              ref={textareaRef}
              dir="ltr"
              spellCheck={false}
              disabled={disabled}
              value={code}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[16rem] w-full resize-y bg-transparent px-3 py-3 text-left font-mono text-[12px] leading-5 text-emerald-100 outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}
